// Deterministic mock data for layout validation (?mock=1). NOT used in normal
// operation — the page calls the real /analytics endpoints and shows empty states
// when there is no data. This only exists so the layout can be reviewed without a
// seeded database.
import type {
    SummaryResponse, TimeseriesResponse, BreakdownResponse,
    LeaderboardResponse, PolygonsResponse, PolygonRow,
} from './types';

const COMMODITIES = ['cocoa', 'coffee', 'palm_oil', 'rubber', 'soy', 'timber'];
const COUNTRIES = ['Ecuador', 'Colombia', 'Peru', 'Brazil', 'Ivory Coast'];
const PRODUCERS = ['Finca Aurora', 'Coop. El Oro', 'Hacienda Vélez', 'Asoprocacao', 'Grupo Manabí', 'San Carlos'];

// tiny seeded PRNG so the mock is stable across renders
function rng(seed: number) {
    let s = seed;
    return () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff; };
}
const r = rng(42);
const pick = <T,>(a: T[]) => a[Math.floor(r() * a.length)];
const round = (n: number, d = 1) => Math.round(n * 10 ** d) / 10 ** d;

export function mockSummary(): SummaryResponse {
    return {
        kpis: [
            { key: 'area_monitored_ha', label: 'Area monitored', value: 18432.5, unit: 'ha', good_when: 'up', delta_pct: 6.2 },
            { key: 'compliance_rate', label: 'Compliance rate', value: 81.4, unit: '%', good_when: 'up', delta_pct: 2.1 },
            { key: 'area_deforested_ha', label: 'Deforestation detected', value: 342.8, unit: 'ha', good_when: 'down', delta_pct: 14.7 },
            { key: 'high_risk_count', label: 'High-risk lots', value: 23, unit: 'count', good_when: 'down', delta_pct: -8.0 },
            { key: 'polygons_analyzed', label: 'Lots analyzed', value: 214, unit: 'count', good_when: 'up', delta_pct: 11.0 },
            { key: 'avg_ndvi_change', label: 'Avg NDVI change', value: -0.043, unit: 'Δ', good_when: 'up', delta_pct: -3.3 },
        ],
        period: { from: null, to: new Date().toISOString(), preset: 'mock' },
    };
}

export function mockTimeseries(metric: string): TimeseriesResponse {
    const points = Array.from({ length: 13 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (12 - i) * 7);
        const base = metric === 'compliance_rate' ? 75 + r() * 18
            : metric === 'deforestation_pct' ? 4 + r() * 10
            : metric === 'ndvi_change' ? -(0.02 + r() * 0.08)
            : metric === 'jobs' ? Math.floor(4 + r() * 16)
            : 10 + r() * 50;
        return { date: d.toISOString().slice(0, 10), value: round(base, metric === 'ndvi_change' ? 4 : 1), count: Math.floor(4 + r() * 16) };
    });
    return { metric, granularity: 'week', points };
}

export function mockBreakdown(dimension: string): BreakdownResponse {
    const keys = dimension === 'country' ? COUNTRIES
        : dimension === 'status' ? ['compliant', 'non_compliant', 'pending']
        : dimension === 'risk' ? ['low', 'medium', 'high']
        : COMMODITIES;
    return {
        dimension,
        groups: keys.map((key) => {
            const count = Math.floor(8 + r() * 60);
            return {
                key, count,
                area_ha: round(count * (40 + r() * 120)),
                area_deforested_ha: round(count * r() * 4, 2),
                high_risk_count: Math.floor(r() * count * 0.3),
                compliance_rate: round(60 + r() * 38, 1),
            };
        }).sort((a, b) => b.count - a.count),
    };
}

function mockItem(i: number) {
    const def = round(r() * 45, 1);
    return {
        polygon_id: `mock-${i}`,
        polygon_name: `Lot ${String(i).padStart(3, '0')}`,
        producer_name: pick(PRODUCERS),
        commodity: pick(COMMODITIES),
        country: pick(COUNTRIES),
        area_ha: round(20 + r() * 400),
        deforestation_percentage: def,
        area_deforested_ha: round(def * 2, 2),
        ndvi_change: round(-(r() * 0.25), 4),
        compliance_status: def > 15 ? 'non_compliant' : def > 5 ? 'pending' : 'compliant',
        risk_level: def > 15 ? 'high' : def > 5 ? 'medium' : 'low',
    };
}

export function mockLeaderboard(metric: string): LeaderboardResponse {
    const items = Array.from({ length: 10 }, (_, i) => mockItem(i + 1))
        .sort((a, b) => (b.deforestation_percentage || 0) - (a.deforestation_percentage || 0));
    return { metric, items };
}

// Build a FeatureCollection of small square lots around each row's centroid,
// so the risk map has geometries to render in ?mock=1 mode (ids match the rows).
export function squaresFromRows(rows: PolygonRow[]) {
    const d = 0.02;
    return {
        type: 'FeatureCollection',
        features: rows
            .filter((r) => r.centroid_lat != null && r.centroid_lon != null)
            .map((r) => {
                const la = r.centroid_lat as number, lo = r.centroid_lon as number;
                return {
                    type: 'Feature',
                    properties: { id: r.polygon_id },
                    geometry: {
                        type: 'Polygon',
                        coordinates: [[[lo - d, la - d], [lo - d, la + d], [lo + d, la + d], [lo + d, la - d], [lo - d, la - d]]],
                    },
                };
            }),
    };
}

export function mockPolygons(): PolygonsResponse {
    const items = Array.from({ length: 40 }, (_, i) => ({
        ...mockItem(i + 1),
        region: 'Guayas',
        confidence_level: pick(['high', 'medium', 'low']),
        last_analyzed: new Date(Date.now() - r() * 7776e6).toISOString(),
        // scatter coords around Ecuador for the map widget
        centroid_lat: round(-2.2 + r() * 2.4, 4),
        centroid_lon: round(-80 + r() * 2.4, 4),
    })).sort((a, b) => (b.deforestation_percentage || 0) - (a.deforestation_percentage || 0));
    return { count: items.length, items };
}
